import { IAPIResponseError, IError } from '@checkout/errors';
import {
  IAccountDetailsChangeSuccessPayload,
  ILogInSuccessPayload,
  ILogOutSuccessPayload,
  IOrdersHistorySuccessPayload,
  IPasswordChangeSuccessPayload,
  IPasswordRecoverySuccessPayload,
  IPasswordResetSuccessPayload,
  IRegisterSuccessPayload,
} from '@checkout/payloadTypes';
import { RootState } from '@checkout/state';
import { AxiosResponse } from 'axios';
import { Epic } from 'redux-observable';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/mergeMap';
import { concat as concat$ } from 'rxjs/observable/concat';
import { fromPromise as fromPromise$ } from 'rxjs/observable/fromPromise';
import { of as of$ } from 'rxjs/observable/of';
import { zip as zip$ } from 'rxjs/observable/zip';
import { checkoutApi } from 'ys-analytics';
import ActionTypes from '../actions/actionTypes';
import {
  AccountDetailsChangeRequestAction,
  LogInRequestAction,
  LogOutRequestAction,
  OrdersHistoryRequestAction,
  PasswordChangeRequestAction,
  PasswordRecoveryRequestAction,
  PasswordResetRequestAction,
  RegisterRequestAction,
  RegisterSuccessAction,
  RootAction,
} from '../actions/actionTypes.d';
import {
  accountDetailsChangeSuccess,
  customerClubFetchSuccess,
  logInSuccess,
  logOutSuccess,
  ordersHistorySuccess,
  passwordChangeSuccess,
  passwordResetSuccess,
  registerSuccess,
  resetErrors,
  resetNotifications,
  responseError,
  responseNotifications,
  setErrors,
  toggleMembershipOverlay,
} from '../reducers/checkout';
import { rest } from '../utils/apiUtils';
import convertMessages from '../utils/convertErrors';
import convertNotifications from '../utils/convertNotifications';
import { logger } from '../utils/logger';
import { CustomerClubFetchRequestAction } from './../actions/actionTypes.d';
import { countryChangeRequest, countryStateChangeRequest, passwordRecoverySuccess } from './../reducers/checkout';
import { ICustomerClubFetchSuccessPayload } from './../typings/payloadTypes.d';

const { eeCheckoutSteps, fbCompleteRegistration } = checkoutApi;

export const logInEpic: Epic<RootAction, RootAction, RootState> = (action$, state$) =>
  action$.ofType(ActionTypes.LOG_IN_REQUEST)
    .mergeMap((action: LogInRequestAction) => {
      const { email, password } = action.payload;

      return fromPromise$(rest.httpPost(`/login/${email}`, { password }))
        .switchMap((response: AxiosResponse<ILogInSuccessPayload>) => {
          const { loggedIn, selection } = response.data;
          // No needed countryChangeRequest, because API is returning all data based on logged in user data

          /* if (loggedIn && loggedIn.country && loggedIn.state && loggedIn.country !==  location.country) {
            return concat$(
              of$(countryStateChangeRequest({
                countryCode: loggedIn.country,
                stateCode: loggedIn.state,
                formName: 'shippingAddress'
              })),
              zip$(
                action$.ofType(ActionTypes.COUNTRY_STATE_CHANGE_SUCCESS).take(1), // Wait for COUNTRY_CHANGE_SUCCESS
              ).flatMapTo(concat$(
                of$(toggleMembershipOverlay({ isOpen: false })),
                of$(logInSuccess(response.data))
              )),
            );
          } else if (loggedIn && loggedIn.country && loggedIn.country !==  location.country) {
            return concat$(
              of$(countryChangeRequest({
                countryCode: loggedIn.country,
                formName: 'shippingAddress'
              })),
              zip$(
                action$.ofType(ActionTypes.COUNTRY_CHANGE_SUCCESS).take(1), // Wait for COUNTRY_CHANGE_SUCCESS
              ).flatMapTo(concat$(
                of$(toggleMembershipOverlay({ isOpen: false })),
                of$(logInSuccess(response.data))
              )),
            );
          } */

          if (loggedIn && window.location.pathname.includes('selection')) {
            // tslint:disable-next-line:no-console
            try {
              eeCheckoutSteps.loginSuccess(selection.currency);
            } catch (err) {
              // tslint:disable-next-line:no-console
              console.error('Enhanced Ecommerce error: ', err);
            }
          }

          return concat$(
            of$(toggleMembershipOverlay({ isOpen: false })),
            of$(logInSuccess(response.data))
          );
        })
        .catch((error: IError) => {
          logger(error);
          if (error.response && error.response.data) {
            const convertedMessages = convertMessages(error.response.data, state$.value.checkout.intlData, 'inline');
            const responseApiErrorPayload: IAPIResponseError = {
              errors: convertedMessages,
              selection: error.response.data.selection
            };
            return of$(responseError(responseApiErrorPayload));
          }
          return of$(setErrors(error));
        })
        .startWith(
          resetErrors(),
          resetNotifications()
        );
    });

export const logOutEpic: Epic<RootAction, RootAction, RootState> = (action$, state$) =>
  action$.ofType(ActionTypes.LOG_OUT_REQUEST)
    .mergeMap((action: LogOutRequestAction) => {
      return fromPromise$(rest.httpPost('/logout'))
        .flatMap((response: AxiosResponse<ILogOutSuccessPayload>) => {
          return of$(logOutSuccess(response.data));
        })
        // TODO: think if in case of 406 not access should we logout user
        .catch((error: IError) => {
          logger(error);
          return of$(); // TODO: handle logOutError
        })
        .startWith(
          resetErrors(),
          resetNotifications()
        );
    });

export const registerEpic: Epic<RootAction, RootAction, RootState> = (action$, state$) =>
  action$.ofType(ActionTypes.REGISTER_REQUEST)
    .mergeMap((action: RegisterRequestAction) => {
      return fromPromise$(rest.httpPost('/register', { ...action.payload }))
        .flatMap((response: AxiosResponse<IRegisterSuccessPayload>) => {
          const { loggedIn } = response.data;

          // Check with Centra if countryStateChangeRequest is needed, API should return all data based on registered in user data
          if (loggedIn && loggedIn.country && loggedIn.state) {
            return concat$(
              of$(countryStateChangeRequest({
                countryCode: loggedIn.country,
                stateCode: loggedIn.state,
                formName: 'shippingAddress'
              })),
              zip$(
                action$.ofType(ActionTypes.COUNTRY_STATE_CHANGE_SUCCESS).take(1), // Wait for COUNTRY_STATE_CHANGE_SUCCESS
              ).flatMapTo(concat$(
                of$(toggleMembershipOverlay({ isOpen: false })),
                of$(registerSuccess(response.data))
              )),
            );
          } else if (loggedIn && loggedIn.country) {
            return concat$(
              of$(countryChangeRequest({
                countryCode: loggedIn.country,
                formName: 'shippingAddress'
              })),
              zip$(
                action$.ofType(ActionTypes.COUNTRY_CHANGE_SUCCESS).take(1), // Wait for COUNTRY_CHANGE_SUCCESS
              ).flatMapTo(concat$(
                of$(toggleMembershipOverlay({ isOpen: false })),
                of$(registerSuccess(response.data))
              )),
            );
          }
          return concat$(
            of$(toggleMembershipOverlay({ isOpen: false })),
            of$(registerSuccess(response.data))
          );
        })
        .catch((error: IError) => {
          logger(error);
          if (error.response && error.response.data) {
            const convertedMessages = convertMessages(error.response.data, state$.value.checkout.intlData, 'inline');
            const responseApiErrorPayload: IAPIResponseError = {
              errors: convertedMessages,
              selection: error.response.data.selection,
            };
            return of$(responseError(responseApiErrorPayload));
          }
          return of$(setErrors(error));
        })
        .startWith(
          resetErrors(),
          resetNotifications()
        );
    });

export const passwordChangeEpic: Epic<RootAction, RootAction, RootState> = (action$, state$) =>
  action$.ofType(ActionTypes.PASSWORD_CHANGE_REQUEST)
    .mergeMap((action: PasswordChangeRequestAction) => {
      const { password, newPassword } = action.payload;

      return fromPromise$(rest.httpPut('/password', { password, newPassword }))
        .flatMap((response: AxiosResponse<IPasswordChangeSuccessPayload>) => {
          const notification = convertNotifications({ type: 'change_success', intlData: state$.value.checkout.intlData });
          return concat$(
            of$(passwordChangeSuccess(response.data)),
            of$(responseNotifications(notification))
          );
        })
        .catch((error: IError) => {
          logger(error);
          if (error.response && error.response.data) {
            const convertedMessages = convertMessages(error.response.data, state$.value.checkout.intlData, 'inline');
            const responseApiErrorPayload: IAPIResponseError = {
              errors: convertedMessages,
              selection: error.response.data.selection,
            };
            return of$(responseError(responseApiErrorPayload));
          }
          return of$(setErrors(error));
        })
        .startWith(
          resetErrors(),
          resetNotifications()
        );
    });

export const passwordRecoveryEpic: Epic<RootAction, RootAction, RootState> = (action$, state$) =>
  action$.ofType(ActionTypes.PASSWORD_RECOVERY_REQUEST)
    .mergeMap((action: PasswordRecoveryRequestAction) => {
      const { email, linkUri } = action.payload;

      return fromPromise$(rest.httpPost(`/password-reset-email/${email}`, { linkUri }))
        .flatMap((response: AxiosResponse<IPasswordRecoverySuccessPayload>) => {
          const notification = convertNotifications({ type: 'recovery_success', intlData: state$.value.checkout.intlData });
          return concat$(
            of$(passwordRecoverySuccess()),
            of$(responseNotifications(notification))
          );
        })
        .catch((error: IError) => {
          logger(error);
          if (error.response && error.response.data) {
            const convertedMessages = convertMessages(error.response.data, state$.value.checkout.intlData, 'inline');
            const responseApiErrorPayload: IAPIResponseError = {
              errors: convertedMessages,
              selection: error.response.data.selection,
            };
            return of$(responseError(responseApiErrorPayload));
          }
          return of$(setErrors(error));
        })
        .startWith(
          resetErrors(),
          resetNotifications()
        );
    });

export const passwordResetEpic: Epic<RootAction, RootAction, RootState> = (action$, state$) =>
  action$.ofType(ActionTypes.PASSWORD_RESET_REQUEST)
    .mergeMap((action: PasswordResetRequestAction) => {
      const { newPassword, i, id } = action.payload;

      return fromPromise$(rest.httpPost('/password-reset', { newPassword, i, id }))
        .flatMap((response: AxiosResponse<IPasswordResetSuccessPayload>) => {
          const notification = convertNotifications({ type: 'change_success', intlData: state$.value.checkout.intlData });
          return concat$(
            of$(passwordResetSuccess(response.data)),
            of$(responseNotifications(notification))
          );
        })
        .catch((error: IError) => {
          logger(error);
          if (error.response && error.response.data) {
            const convertedMessages = convertMessages(error.response.data, state$.value.checkout.intlData, 'inline');
            const responseApiErrorPayload: IAPIResponseError = {
              errors: convertedMessages,
              selection: error.response.data.selection,
            };
            return of$(responseError(responseApiErrorPayload));
          }
          return of$(setErrors(error));
        })
        .startWith(
          resetErrors(),
          resetNotifications()
        );
    });

export const accountDetailsEpic: Epic<RootAction, RootAction, RootState> = (action$, state$) =>
  action$.ofType(ActionTypes.ACCOUNT_DETAILS_CHANGE_REQUEST)
    .mergeMap((action: AccountDetailsChangeRequestAction) => {
      return fromPromise$(rest.httpPut('/customer/update', { ...action.payload }))
        .flatMap((response: AxiosResponse<IAccountDetailsChangeSuccessPayload>) => {
          const { loggedIn } = response.data;
          const notification = convertNotifications({ type: 'update_details_success', intlData: state$.value.checkout.intlData });

          // Check with Centra if countryStateChangeRequest is needed, API should return all data based on registered in user data
          if (loggedIn && loggedIn.country && loggedIn.state) {
            return concat$(
              of$(countryStateChangeRequest({
                countryCode: loggedIn.country,
                stateCode: loggedIn.state,
                formName: 'shippingAddress'
              })),
              zip$(
                action$.ofType(ActionTypes.COUNTRY_STATE_CHANGE_SUCCESS).take(1), // Wait for COUNTRY_CHANGE_SUCCESS
              ).flatMapTo(concat$(
                of$(toggleMembershipOverlay({ isOpen: false })),
                of$(accountDetailsChangeSuccess(response.data)),
                of$(responseNotifications(notification))
              )),
            );
          } else if (loggedIn && loggedIn.country) {
            return concat$(
              of$(countryChangeRequest({
                countryCode: loggedIn.country,
                formName: 'shippingAddress'
              })),
              zip$(
                action$.ofType(ActionTypes.COUNTRY_CHANGE_SUCCESS).take(1), // Wait for COUNTRY_CHANGE_SUCCESS
              ).flatMapTo(concat$(
                of$(toggleMembershipOverlay({ isOpen: false })),
                of$(accountDetailsChangeSuccess(response.data)),
                of$(responseNotifications(notification))
              )),
            );
          }
          return concat$(
            of$(toggleMembershipOverlay({ isOpen: false })),
            of$(accountDetailsChangeSuccess(response.data)),
            of$(responseNotifications(notification))
          );
        })
        .catch((error: IError) => {
          logger(error);
          if (error.response && error.response.data) {
            const convertedMessages = convertMessages(error.response.data, state$.value.checkout.intlData, 'inline');
            const responseApiErrorPayload: IAPIResponseError = {
              errors: convertedMessages,
              selection: error.response.data.selection,
            };
            return of$(responseError(responseApiErrorPayload));
          }
          return of$(setErrors(error));
        })
        .startWith(
          resetErrors(),
          resetNotifications()
        );
    });

export const registerSuccessEpic: Epic<RootAction, RootAction, RootState> = (action$, state$) =>
  action$.ofType(ActionTypes.REGISTER_SUCCESS)
    .mergeMap((action: RegisterSuccessAction) => {
      try {
        fbCompleteRegistration();
      } catch (err) {
        // tslint:disable-next-line:no-console
        console.error('Enhanced Ecommerce error: ', err);
      }
      if (!window.location.pathname.includes('/account')) {
        window.location.href = '/account';
      }
      return of$();
    });

export const ordersHistoryEpic: Epic<RootAction, RootAction, RootState> = (action$, state$) =>
  action$.ofType(ActionTypes.ORDERS_HISTORY_REQUEST)
    .mergeMap((action: OrdersHistoryRequestAction) => {
      return fromPromise$(rest.httpPost('/orders'))
        .flatMap((response: AxiosResponse<IOrdersHistorySuccessPayload>) => {
          return of$(ordersHistorySuccess(response.data));
        })
        .catch((error: IError) => {
          logger(error);
          if (error.response && error.response.data) {
            const convertedMessages = convertMessages(error.response.data, state$.value.checkout.intlData, 'inline');
            const responseApiErrorPayload: IAPIResponseError = {
              errors: convertedMessages,
              selection: error.response.data.selection,
            };
            return of$(responseError(responseApiErrorPayload));
          }
          return of$(setErrors(error));
        })
        .startWith(
          resetErrors(),
          resetNotifications()
        );
    });

export const customerClubEpic: Epic<RootAction, RootAction, RootState> = (action$, state$) =>
  action$.ofType(ActionTypes.CUSTOMER_CLUB_FETCH_REQUEST)
    .mergeMap((action: CustomerClubFetchRequestAction) => {
      return fromPromise$(rest.httpGet('/customer'))
        .flatMap((response: AxiosResponse<ICustomerClubFetchSuccessPayload>) => {
          return of$(customerClubFetchSuccess(response.data));
        })
        .catch((error: IError) => {
          logger(error);
          if (error.response && error.response.data) {
            const convertedMessages = convertMessages(error.response.data, state$.value.checkout.intlData, 'inline');
            const responseApiErrorPayload: IAPIResponseError = {
              errors: convertedMessages,
              selection: error.response.data.selection,
            };
            return of$(responseError(responseApiErrorPayload));
          }
          return of$(setErrors(error));
        })
        .startWith(
          resetErrors(),
          resetNotifications()
        );
    });



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/epics/membership.ts